<script setup lang="ts">
import {
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  FileText as FileTextIcon,
  House as HouseIcon,
  Keyboard as KeyboardIcon,
  LogOut as LogOutIcon,
  RefreshCw as RefreshCwIcon,
  Settings as SettingsIcon,
  Ticket as TicketIcon,
  WifiOff as WifiOffIcon,
} from '@lucide/vue'
import { computed, ref } from 'vue'

import BaseAvatar from '@/components/ui/BaseAvatar.vue'
import BaseMenuRow from '@/components/ui/BaseMenuRow.vue'
import { formatByteSize } from '@/services/formattingService'

// Shared account-menu rows for both the desktop dropdown (dense) and the mobile ProfileSheet
// (comfortable, ≥44px touch targets). Behaviour lives in the parent (UserMenu) — this component
// only renders the rows and emits intent.
const props = withDefaults(
  defineProps<{
    userName: string
    userEmail: string
    /** Uppercase initials for the fallback avatar when no photo is set. */
    initials: string
    /** Optional profile photo URL; falls back to initials when absent. */
    avatarUrl?: string | null
    /** Optional short bio (Komoot-style personality line), shown under the name when present. */
    bio?: string | null
    recordingSupported?: boolean
    isTauriApp?: boolean
    offlineSize?: number
    isTouchDevice?: boolean
    isLoading?: boolean
    /** Compact spacing for the desktop dropdown; mobile leaves this false for larger targets. */
    dense?: boolean
  }>(),
  {
    avatarUrl: null,
    bio: null,
    recordingSupported: false,
    isTauriApp: false,
    offlineSize: 0,
    isTouchDevice: false,
    isLoading: false,
    dense: false,
  },
)

const emit = defineEmits<{
  (e: 'edit-profile'): void
  (e: 'home'): void
  (e: 'record'): void
  (e: 'invitations'): void
  (e: 'offline'): void
  (e: 'sync'): void
  (e: 'static-site', pageId: string): void
  (e: 'logout'): void
}>()

const isLegalOpen = ref(false)

const rowSize = computed<'dense' | 'comfortable'>(() => (props.dense ? 'dense' : 'comfortable'))
const userInfoClass = computed(() =>
  props.dense ? 'px-4 py-3 border-b border-gray-100' : 'px-4 py-4 border-b border-gray-100',
)
const legalItemClass = computed(() =>
  [
    'w-full flex items-center pl-10 pr-4 text-gray-600 hover:bg-gray-100 transition-colors hover:cursor-pointer',
    props.dense ? 'py-1.5 text-sm' : 'py-2.5 text-[15px]',
  ].join(' '),
)
</script>

<template>
  <div>
    <!-- User identity card: avatar + name + (email / bio) -->
    <div :class="userInfoClass" class="flex items-center gap-3">
      <BaseAvatar :size="dense ? 'md' : 'lg'">
        <img v-if="avatarUrl" :src="avatarUrl" alt="" class="h-full w-full object-cover" />
        <span v-else>{{ initials }}</span>
      </BaseAvatar>
      <div class="min-w-0">
        <p class="truncate font-semibold text-gray-900" :class="dense ? 'text-sm' : 'text-base'">
          {{ userName }}
        </p>
        <p v-if="userEmail" class="truncate text-xs text-gray-500">{{ userEmail }}</p>
        <p v-else class="text-xs text-gray-500">Logged in</p>
        <p v-if="bio" class="mt-0.5 line-clamp-2 text-xs text-gray-600">{{ bio }}</p>
      </div>
    </div>

    <!-- Profile edit -->
    <BaseMenuRow
      :icon="SettingsIcon"
      :size="rowSize"
      role="menuitem"
      :disabled="isLoading"
      @click="emit('edit-profile')"
    >
      Edit Profile
    </BaseMenuRow>

    <div class="border-t border-gray-100 mt-1 pt-1">
      <BaseMenuRow :icon="HouseIcon" :size="rowSize" role="menuitem" @click="emit('home')">
        Home
      </BaseMenuRow>
      <BaseMenuRow
        v-if="recordingSupported"
        :icon="CircleIcon"
        icon-class="text-red-600 fill-current"
        :size="rowSize"
        role="menuitem"
        @click="emit('record')"
      >
        Record
      </BaseMenuRow>
      <BaseMenuRow :icon="TicketIcon" :size="rowSize" role="menuitem" @click="emit('invitations')">
        Invitations
      </BaseMenuRow>
      <BaseMenuRow
        v-if="isTauriApp"
        :icon="WifiOffIcon"
        :size="rowSize"
        role="menuitem"
        @click="emit('offline')"
      >
        Offline data
        <template v-if="offlineSize > 0" #trailing>
          <span class="text-xs text-gray-400 tabular-nums">{{ formatByteSize(offlineSize) }}</span>
        </template>
      </BaseMenuRow>
      <BaseMenuRow :icon="RefreshCwIcon" :size="rowSize" role="menuitem" @click="emit('sync')">
        Sync settings
      </BaseMenuRow>
    </div>

    <!-- Static page links -->
    <div class="border-t border-gray-100 mt-1 pt-1">
      <BaseMenuRow
        :icon="FileTextIcon"
        :size="rowSize"
        role="menuitem"
        @click="emit('static-site', 'about')"
      >
        About
      </BaseMenuRow>
      <BaseMenuRow
        v-if="!isTouchDevice"
        :icon="KeyboardIcon"
        :size="rowSize"
        role="menuitem"
        @click="emit('static-site', 'keyboard-shortcuts')"
      >
        Keyboard Shortcuts
      </BaseMenuRow>
      <!-- Legal sub-section -->
      <BaseMenuRow
        :icon="FileTextIcon"
        :size="rowSize"
        role="menuitem"
        @click="isLegalOpen = !isLegalOpen"
      >
        Legal
        <template #trailing>
          <ChevronRightIcon
            class="w-3 h-3 transition-transform duration-150"
            :class="{ 'rotate-90': isLegalOpen }"
          />
        </template>
      </BaseMenuRow>
      <div v-if="isLegalOpen" class="bg-gray-50">
        <button
          :class="legalItemClass"
          role="menuitem"
          @click="emit('static-site', 'terms-of-use')"
        >
          Terms of Use
        </button>
        <button
          :class="legalItemClass"
          role="menuitem"
          @click="emit('static-site', 'eu-data-protection')"
        >
          Data Protection
        </button>
        <button
          :class="legalItemClass"
          role="menuitem"
          @click="emit('static-site', 'privacy-policy')"
        >
          Privacy
        </button>
        <button :class="legalItemClass" role="menuitem" @click="emit('static-site', 'imprint')">
          Imprint
        </button>
      </div>
    </div>

    <!-- Logout -->
    <div class="border-t border-gray-100 mt-1 pt-1">
      <BaseMenuRow
        :icon="LogOutIcon"
        :size="rowSize"
        role="menuitem"
        :disabled="isLoading"
        @click="emit('logout')"
      >
        {{ isLoading ? 'Logging out...' : 'Log out' }}
      </BaseMenuRow>
    </div>
  </div>
</template>
